/*
 * Copyright (c) 2023 Bolmate B.V.
 */
import './style/trialEnd.scss';

import {Loader, voidFunction, WithBackground} from '@dvrd/dvr-controls';

import React, {MouseEventHandler} from 'react';

import {useTranslation} from 'react-i18next';

import {BolmateButton} from '../controls';

import FailedPayment from '../../models/failedPaymentModel';

import Image from '../misc/image';

interface Props {
    onProceed: MouseEventHandler;
    active: boolean;
    failedPayment: FailedPayment | null;
    loading: boolean;
    buttonDisabled: boolean;
}

export default function TrialEnd(props: Props) {
    const {t} = useTranslation();
    const {active, onProceed, failedPayment, loading, buttonDisabled} = props;

    function renderMessage() {
        if (failedPayment) {
            const {paymentUrl, periodFromMoment, periodToMoment} = failedPayment;
            let subLabel = t('trialEnd.yourSubscription', 'je abonnement');
            if (periodToMoment && periodFromMoment) {
                const dateFormat: string = periodFromMoment.isAt(periodToMoment, 'year') ? 'D MMMM' : 'D MMMM YYYY';
                subLabel = `${t('trialEnd.periodPrefix', 'de periode')} ${periodFromMoment.format(dateFormat)} ${t('trialEnd.periodSeparator', 't/m')} ${periodToMoment.format(
                    dateFormat
                )} ${t('trialEnd.periodSuffix', 'van je abonnement')}`;
            }
            if (paymentUrl)
                return (
                    <p className='message-container'>
                        {t('trialEnd.debitFailedFor', 'De automatische incasso voor')} {subLabel}{' '}
                        {t('trialEnd.failed', 'is mislukt.')}
                        <br />
                        {t('trialEnd.noProblem', 'Geen probleem, kan gebeuren :)')}
                        <br />
                        <br />
                        {t(
                            'trialEnd.emailsPaused',
                            'Er worden tijdelijk geen mails naar je klanten verzonden en geen facturen geüpload.'
                        )}
                        <br />
                        {t('trialEnd.newInvoiceCreated', 'We hebben een nieuwe factuur gemaakt,')}
                        <br />
                        {t('trialEnd.viewAndPay', 'deze kun je via onderstaande knop inzien en betalen.')}
                        <br />
                        {t(
                            'trialEnd.afterPayment',
                            'Na betaling kun je de pagina herladen en pakt het systeem het automatisch weer op!'
                        )}
                    </p>
                );
            else
                return (
                    <p className='message-container'>
                        {t('trialEnd.debitFailedFor', 'De automatische incasso voor')} {subLabel}{' '}
                        {t('trialEnd.failed', 'is mislukt.')}
                        <br />
                        {t('trialEnd.noProblem', 'Geen probleem, kan gebeuren :)')}
                        <br />
                        <br />
                        {t(
                            'trialEnd.emailsPaused',
                            'Er worden tijdelijk geen mails naar je klanten verzonden en geen facturen geüpload.'
                        )}
                        <br />
                        {t(
                            'trialEnd.generateNewInvoice',
                            'Via onderstaande knop kan je een nieuwe factuur laten genereren.'
                        )}
                        <br />
                        {t('trialEnd.redirectedToPayment', 'Je wordt automatisch doorgestuurd naar de betaalpagina.')}
                        <br />
                        {t(
                            'trialEnd.afterPayment',
                            'Na betaling kun je de pagina herladen en pakt het systeem het automatisch weer op!'
                        )}
                    </p>
                );
        } else
            return (
                <p className='message-container'>
                    {t('trialEnd.subscriptionEnded', 'Je abonnement is afgelopen.')}
                    <br />
                    {t(
                        'trialEnd.emailsPaused',
                        'Er worden tijdelijk geen mails naar je klanten verzonden en geen facturen geüpload.'
                    )}
                    <br />
                    {t('trialEnd.checkPriceResume', 'Check je prijs, koppel een betaalmethode en hervat je gebruik.')}
                </p>
            );
    }

    function renderButton() {
        let label: string;
        if (failedPayment?.paymentUrl) label = t('trialEnd.viewAndPayInvoice', 'Factuur inzien en betalen');
        else if (failedPayment) label = t('trialEnd.resumeSubscription', 'Abonnement hervatten');
        else label = t('trialEnd.goToSettings', 'Ga naar instellingen');
        return <BolmateButton onClick={onProceed} label={label} disabled={buttonDisabled} />;
    }

    return (
        <WithBackground
            active={active}
            onClose={voidFunction}
            backgroundClass='trial-background'
            backgroundColor='transparent'
            zIndex={1300}
            allowBodyScroll
        >
            <div className='trial-end'>
                <div className='header-container'>
                    <label className='header-title'>{t('trialEnd.paused', 'Abonnement is gepauzeerd')}</label>
                </div>
                <div className='body-container'>
                    <Loader active={loading} />
                    <Image name='trial-rocket' className='trial-img' />
                    <label className='body-title'>{t('trialEnd.continueWithBolmate', 'Doorgaan met Bolmate')}</label>
                    {renderMessage()}
                    {renderButton()}
                </div>
            </div>
        </WithBackground>
    );
}
